import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, Users, ChevronDown, ChevronUp, Check, X, Save, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

// ProfileEditor als eigenständige Komponente (außerhalb der Parent-Komponente,
// damit React eine stabile Referenz behält und State nicht bei jedem Re-Render zurücksetzt)
const ProfileEditor = ({ profile, onSave, onCancel, mappingData }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ ...profile });
  const [activeSection, setActiveSection] = useState('general');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      // Wenn "*" ausgewählt wird, toggle zwischen Alle und keine Auswahl
      if (value === '*') {
        if (current.includes('*')) {
          // "Alle erlauben" deaktivieren - leeres Array setzen um Einzelauswahl zu ermöglichen
          return { ...prev, [field]: [] };
        }
        return { ...prev, [field]: ['*'] };
      }
      // Wenn ein spezifischer Wert ausgewählt wird und "*" vorhanden ist, "*" entfernen
      const newArray = current.filter(v => v !== '*');
      if (newArray.includes(value)) {
        // Wert entfernen
        const filtered = newArray.filter(v => v !== value);
        return { ...prev, [field]: filtered };
      } else {
        // Wert hinzufügen
        return { ...prev, [field]: [...newArray, value] };
      }
    });
  };

  const isValueSelected = (field, value) => {
    const current = formData[field] || [];
    return current.includes(value) || current.includes('*');
  };

  const hasFullAccess = (field) => {
    return (formData[field] || []).includes('*');
  };

  const renderMultiSelect = (field, options, labelKey = 'name', valueKey = 'code') => (
    <div className="space-y-2">
      <label className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
        hasFullAccess(field)
          ? isDarkMode ? 'bg-emerald-500/20 border border-emerald-400/30' : 'bg-emerald-50 border border-emerald-200'
          : isDarkMode ? 'bg-gray-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
      }`}>
        <input
          type="checkbox"
          checked={hasFullAccess(field)}
          onChange={() => handleArrayToggle(field, '*')}
          className="rounded"
        />
        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('admin.permissions.allowAll')}
        </span>
      </label>

      {!hasFullAccess(field) && (
        <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 rounded-lg ${
          isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'
        }`}>
          {options.map(option => (
            <label
              key={option[valueKey] || option.id}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                isValueSelected(field, String(option[valueKey] || option.id))
                  ? isDarkMode ? 'bg-blue-500/20 border border-blue-400/30' : 'bg-brand/10 border border-brand/30'
                  : isDarkMode ? 'bg-gray-700/50 hover:bg-gray-600/50' : 'bg-white hover:bg-slate-50 border border-slate-100'
              }`}
            >
              <input
                type="checkbox"
                checked={isValueSelected(field, String(option[valueKey] || option.id))}
                onChange={() => handleArrayToggle(field, String(option[valueKey] || option.id))}
                className="rounded"
              />
              <span className={`text-sm truncate ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {option[labelKey] || option.name}
                {option[valueKey] && option[valueKey] !== option[labelKey] && (
                  <span className="text-xs opacity-60 ml-1">({option[valueKey]})</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative rounded-2xl overflow-hidden ${
      isDarkMode ? 'bg-gray-800/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'
    }`}>
      {/* Header */}
      <div className={`p-4 border-b ${isDarkMode ? 'border-white/10 bg-gray-900/50' : 'border-slate-200 bg-slate-50'}`}>
        <input
          type="text"
          placeholder={t('admin.permissions.profileName')}
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className={`text-xl font-semibold w-full bg-transparent border-none outline-none ${
            isDarkMode ? 'text-white placeholder-white/40' : 'text-gray-800 placeholder-gray-400'
          }`}
        />
        <input
          type="text"
          placeholder={t('admin.permissions.description')}
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          className={`text-sm w-full bg-transparent border-none outline-none mt-1 ${
            isDarkMode ? 'text-white/60 placeholder-white/30' : 'text-gray-600 placeholder-gray-400'
          }`}
        />
      </div>

      {/* Section Tabs */}
      <div className={`flex border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
        {[
          { id: 'general', label: t('admin.permissions.tabGeneral') },
          { id: 'org', label: t('admin.permissions.tabOrganization') },
          { id: 'catalogs', label: t('admin.permissions.tabCatalogs') },
          { id: 'functions', label: t('admin.permissions.tabFeatures') }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeSection === section.id
                ? isDarkMode
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10'
                  : 'text-brand border-b-2 border-brand bg-brand/5'
                : isDarkMode
                  ? 'text-white/60 hover:text-white/80'
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div className="p-4 space-y-4">
        {activeSection === 'general' && (
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.maxOrderValue')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t('common.unlimited')}
                  value={formData.max_order_value || ''}
                  onChange={(e) => handleChange('max_order_value', e.target.value ? parseFloat(e.target.value) : null)}
                  className={`flex-1 px-3 py-2 rounded-lg ${
                    isDarkMode
                      ? 'bg-gray-700/50 border border-white/10 text-white'
                      : 'bg-white border border-slate-200 text-slate-800'
                  }`}
                />
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>EUR</span>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.docTypes')}
              </label>
              <div className="flex flex-wrap gap-2">
                {['NB', 'UB', 'FO'].map(docType => (
                  <label
                    key={docType}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                      (formData.allowed_document_types || []).includes(docType)
                        ? isDarkMode ? 'bg-blue-500/20 border border-blue-400/30' : 'bg-brand/10 border border-brand/30'
                        : isDarkMode ? 'bg-gray-700/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(formData.allowed_document_types || []).includes(docType)}
                      onChange={() => {
                        const current = formData.allowed_document_types || [];
                        if (current.includes(docType)) {
                          handleChange('allowed_document_types', current.filter(d => d !== docType));
                        } else {
                          handleChange('allowed_document_types', [...current, docType]);
                        }
                      }}
                      className="rounded"
                    />
                    <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>{docType}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'org' && (
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.plants')}
              </label>
              {renderMultiSelect('allowed_plants', mappingData.plants, 'name', 'code')}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.costCenters')}
              </label>
              {renderMultiSelect('allowed_cost_centers', mappingData.costCenters, 'name', 'code')}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.purchasingOrgs')}
              </label>
              {renderMultiSelect('allowed_purchasing_orgs', mappingData.purchasingOrgs, 'name', 'code')}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.purchasingGroups')}
              </label>
              {renderMultiSelect('allowed_purchasing_groups', mappingData.purchasingGroups, 'name', 'code')}
            </div>
          </div>
        )}

        {activeSection === 'catalogs' && (
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.internalShops')}
              </label>
              {renderMultiSelect('allowed_internal_shops', mappingData.internalShops, 'name', 'id')}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                {t('admin.permissions.ociShops')}
              </label>
              {renderMultiSelect('allowed_oci_shops', mappingData.ociShops, 'name', 'id')}
            </div>
          </div>
        )}

        {activeSection === 'functions' && (
          <div className="space-y-3">
            {[
              { field: 'can_access_procurement', label: t('admin.permissions.accessProcurement'), description: t('admin.permissions.accessProcurementDesc') },
              { field: 'can_access_warehouse', label: t('admin.permissions.accessWarehouse'), description: t('admin.permissions.accessWarehouseDesc') },
              { field: 'can_access_einvoicing', label: t('admin.permissions.accessEInvoicing'), description: t('admin.permissions.accessEInvoicingDesc') },
              { field: 'can_access_pm', label: t('admin.permissions.accessPm'), description: t('admin.permissions.accessPmDesc') },
              { field: 'can_access_workflow_editor', label: t('admin.permissions.accessWorkflow'), description: t('admin.permissions.accessWorkflowDesc') },
              { field: 'can_create_banf', label: t('admin.permissions.createBanf'), description: t('admin.permissions.createBanfDesc') },
              { field: 'can_approve_banf', label: t('admin.permissions.approveBanf'), description: t('admin.permissions.approveBanfDesc') },
              { field: 'can_manage_favorites', label: t('admin.permissions.manageFavorites'), description: t('admin.permissions.manageFavoritesDesc') },
              { field: 'can_edit_catalog', label: t('admin.permissions.editCatalog'), description: t('admin.permissions.editCatalogDesc') },
              { field: 'can_access_order_confirmation', label: t('admin.permissions.accessOrderConfirmation'), description: t('admin.permissions.accessOrderConfirmationDesc') }
            ].map(({ field, label, description }) => (
              <label
                key={field}
                className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                  formData[field]
                    ? isDarkMode ? 'bg-emerald-500/10 border border-emerald-400/20' : 'bg-emerald-50 border border-emerald-200'
                    : isDarkMode ? 'bg-gray-800/30 border border-white/5 hover:border-white/10' : 'bg-slate-50 border border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData[field] || false}
                  onChange={(e) => handleChange(field, e.target.checked)}
                  className="mt-1 rounded"
                />
                <div>
                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{label}</div>
                  <div className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{description}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className={`p-4 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/30' : 'border-slate-200 bg-slate-50'} flex justify-end gap-3`}>
        <button
          onClick={onCancel}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            isDarkMode
              ? 'bg-gray-700/50 text-white/80 hover:bg-gray-600/50'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <X className="w-4 h-4 inline mr-2" />
          {t('common.cancel')}
        </button>
        <button
          onClick={() => onSave(formData)}
          disabled={!formData.name}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            formData.name
              ? isDarkMode ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-brand text-white hover:bg-[#1D4ED8]'
              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
          }`}
        >
          <Save className="w-4 h-4 inline mr-2" />
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};

const PermissionProfilesSettings = ({ user, showSuccess, showError, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(null);
  const [expandedProfile, setExpandedProfile] = useState(null);

  // Mapping-Daten für Dropdowns
  const [mappingData, setMappingData] = useState({
    plants: [],
    costCenters: [],
    purchasingOrgs: [],
    purchasingGroups: [],
    internalShops: [],
    ociShops: []
  });

  // Leeres Profil-Template
  const emptyProfile = {
    name: '',
    description: '',
    allowed_plants: ['*'],
    allowed_cost_centers: ['*'],
    allowed_purchasing_orgs: ['*'],
    allowed_purchasing_groups: ['*'],
    allowed_internal_shops: ['*'],
    allowed_oci_shops: ['*'],
    allowed_document_types: ['NB'],
    max_order_value: null,
    can_access_procurement: true,
    can_access_warehouse: false,
    can_access_einvoicing: false,
    can_access_pm: false,
    can_access_workflow_editor: false,
    can_create_banf: true,
    can_approve_banf: false,
    can_manage_favorites: true,
    can_edit_catalog: false,
    can_access_order_confirmation: false
  };

  // Profile laden
  const loadProfiles = async () => {
    try {
      const response = await fetch('/api/permission-profiles', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data);
      } else {
        showError(t('admin.permissions.loadError'));
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      showError(t('admin.permissions.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Mapping-Daten laden
  const loadMappingData = async () => {
    try {
      // Werke laden
      const plantsRes = await fetch('/api/admin/mappings/plant', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (plantsRes.ok) {
        const plants = await plantsRes.json();
        setMappingData(prev => ({ ...prev, plants }));
      }

      // Kostenstellen laden
      const costCentersRes = await fetch('/api/admin/mappings/cost_center', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (costCentersRes.ok) {
        const costCenters = await costCentersRes.json();
        setMappingData(prev => ({ ...prev, costCenters }));
      }

      // Einkaufsorganisationen laden
      const purchasingOrgsRes = await fetch('/api/admin/mappings/purchasing_org', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (purchasingOrgsRes.ok) {
        const purchasingOrgs = await purchasingOrgsRes.json();
        setMappingData(prev => ({ ...prev, purchasingOrgs }));
      }

      // Einkäufergruppen laden
      const purchasingGroupsRes = await fetch('/api/admin/mappings/purchasing_group', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (purchasingGroupsRes.ok) {
        const purchasingGroups = await purchasingGroupsRes.json();
        setMappingData(prev => ({ ...prev, purchasingGroups }));
      }

      // Interne Shops laden
      const internalShopsRes = await fetch('/api/internal-shops', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (internalShopsRes.ok) {
        const internalShops = await internalShopsRes.json();
        setMappingData(prev => ({ ...prev, internalShops }));
      }

      // OCI-Shops laden
      const ociShopsRes = await fetch('/api/oci-shops', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (ociShopsRes.ok) {
        const ociShops = await ociShopsRes.json();
        setMappingData(prev => ({ ...prev, ociShops }));
      }
    } catch (error) {
      console.error('Error loading mapping data:', error);
    }
  };

  useEffect(() => {
    loadProfiles();
    loadMappingData();
  }, [user.token]);

  // Profil speichern
  const handleSaveProfile = async (profile) => {
    try {
      const isNew = !profile.id;
      const url = isNew ? '/api/permission-profiles' : `/api/permission-profiles/${profile.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        showSuccess(isNew ? t('admin.permissions.created') : t('admin.permissions.updated'));
        setEditingProfile(null);
        loadProfiles();
      } else {
        const error = await response.json();
        showError(error.error || t('admin.permissions.loadError'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showError(t('admin.permissions.loadError'));
    }
  };

  // Profil löschen
  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm(t('admin.permissions.deleteConfirm'))) return;

    try {
      const response = await fetch(`/api/permission-profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      if (response.ok) {
        showSuccess(t('admin.permissions.deleted'));
        loadProfiles();
      } else {
        const error = await response.json();
        showError(error.error || t('admin.permissions.loadError'));
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      showError(t('admin.permissions.loadError'));
    }
  };

  // Profil-Karte Komponente
  const ProfileCard = ({ profile }) => {
    const isExpanded = expandedProfile === profile.id;

    const countRestrictions = () => {
      let count = 0;
      if (!profile.allowed_plants?.includes('*')) count += profile.allowed_plants?.length || 0;
      if (!profile.allowed_cost_centers?.includes('*')) count += profile.allowed_cost_centers?.length || 0;
      if (!profile.allowed_purchasing_orgs?.includes('*')) count += profile.allowed_purchasing_orgs?.length || 0;
      return count;
    };

    return (
      <div className={`relative rounded-2xl overflow-hidden transition-all ${
        isDarkMode
          ? 'bg-gradient-to-br from-gray-800/50 via-gray-800/30 to-gray-800/50 border border-white/10'
          : 'bg-white border border-slate-200 shadow-sm'
      }`}>
        {/* Header */}
        <div
          className={`p-4 cursor-pointer ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
          onClick={() => setExpandedProfile(isExpanded ? null : profile.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDarkMode ? 'bg-blue-500/20' : 'bg-brand/10'
              }`}>
                <Shield className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-brand'}`} />
              </div>
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {profile.name}
                </h3>
                {profile.description && (
                  <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                    {profile.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-slate-100 text-slate-600'
              }`}>
                <Users className="w-3 h-3" />
                {profile.user_count || 0}
              </div>
              {countRestrictions() > 0 && (
                <div className={`px-2 py-1 rounded-full text-xs ${
                  isDarkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'
                }`}>
                  {t('admin.permissions.restrictions', { count: countRestrictions() })}
                </div>
              )}
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingProfile(profile); }}
                  className={`p-2 rounded-lg transition-all ${
                    isDarkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteProfile(profile.id); }}
                  className={`p-2 rounded-lg transition-all ${
                    isDarkMode ? 'hover:bg-red-500/20 text-white/60 hover:text-red-400' : 'hover:bg-red-100 text-gray-500 hover:text-red-600'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className={`p-4 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/30' : 'border-slate-100 bg-slate-50'}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Funktionen */}
              <div>
                <h4 className={`text-xs font-semibold uppercase mb-2 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('admin.permissions.tabFeatures')}
                </h4>
                <div className="space-y-1">
                  {[
                    { field: 'can_access_procurement', label: t('admin.permissions.procurement') },
                    { field: 'can_access_warehouse', label: t('admin.permissions.warehouse') },
                    { field: 'can_access_einvoicing', label: t('admin.permissions.einvoicing') },
                    { field: 'can_access_pm', label: t('admin.permissions.pm') },
                    { field: 'can_access_order_confirmation', label: t('admin.permissions.orderConfirmation') },
                    { field: 'can_create_banf', label: t('admin.permissions.createBanf') },
                    { field: 'can_approve_banf', label: t('admin.permissions.approveBanf') }
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center gap-2">
                      {profile[field]
                        ? <Check className="w-4 h-4 text-emerald-500" />
                        : <X className="w-4 h-4 text-red-400" />
                      }
                      <span className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Organisation */}
              <div>
                <h4 className={`text-xs font-semibold uppercase mb-2 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('admin.permissions.tabOrganization')}
                </h4>
                <div className="space-y-1 text-sm">
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.plantsCount', { value: profile.allowed_plants?.includes('*') ? t('common.all') : profile.allowed_plants?.length || 0 })}
                  </div>
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.costCentersCount', { value: profile.allowed_cost_centers?.includes('*') ? t('common.all') : profile.allowed_cost_centers?.length || 0 })}
                  </div>
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.purchasingOrgsCount', { value: profile.allowed_purchasing_orgs?.includes('*') ? t('common.all') : profile.allowed_purchasing_orgs?.length || 0 })}
                  </div>
                </div>
              </div>

              {/* Kataloge */}
              <div>
                <h4 className={`text-xs font-semibold uppercase mb-2 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('admin.permissions.tabCatalogs')}
                </h4>
                <div className="space-y-1 text-sm">
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.internalShopsCount', { value: profile.allowed_internal_shops?.includes('*') ? t('common.all') : profile.allowed_internal_shops?.length || 0 })}
                  </div>
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.ociShopsCount', { value: profile.allowed_oci_shops?.includes('*') ? t('common.all') : profile.allowed_oci_shops?.length || 0 })}
                  </div>
                  <div className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                    {t('admin.permissions.maxValue', { value: profile.max_order_value ? `${profile.max_order_value} EUR` : t('common.unlimited') })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t('admin.permissions.title')}
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
            {t('admin.permissions.subtitle')}
          </p>
        </div>
        <button
          onClick={() => { setEditingProfile({ ...emptyProfile }); }}
          className="px-4 py-2 bg-brand text-white rounded-xl font-medium hover:bg-[#1D4ED8] transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('admin.permissions.newProfile')}
        </button>
      </div>

      {/* Editor Modal */}
      {editingProfile && (
        <ProfileEditor
          profile={editingProfile}
          onSave={handleSaveProfile}
          onCancel={() => setEditingProfile(null)}
          mappingData={mappingData}
        />
      )}

      {/* Profile List */}
      {!editingProfile && (
        <div className="space-y-4">
          {profiles.length === 0 ? (
            <div className={`text-center py-12 rounded-2xl ${isDarkMode ? 'bg-gray-800/30' : 'bg-slate-50 border border-slate-200'}`}>
              <Shield className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-white/20' : 'text-gray-300'}`} />
              <p className={isDarkMode ? 'text-white/60' : 'text-gray-500'}>
                {t('admin.permissions.empty')}
              </p>
              <button
                onClick={() => { setEditingProfile({ ...emptyProfile }); }}
                className="mt-4 px-4 py-2 bg-brand text-white rounded-xl font-medium hover:bg-[#1D4ED8] transition-all"
              >
                {t('admin.permissions.createFirst')}
              </button>
            </div>
          ) : (
            profiles.map(profile => (
              <ProfileCard key={profile.id} profile={profile} />
            ))
          )}
        </div>
      )}

      {/* Info Box */}
      {!editingProfile && profiles.length > 0 && (
        <div className={`p-4 rounded-xl flex items-start gap-3 ${
          isDarkMode ? 'bg-blue-500/10 border border-blue-400/20' : 'bg-blue-50 border border-blue-200'
        }`}>
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
            <strong>{t('admin.permissions.hint')}</strong> {t('admin.permissions.hintText')}
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionProfilesSettings;
